/**
 * Página de resultados e estatísticas do fluxo público
 * Utiliza RPCs server-side para KPIs e listagem paginada
 * Elimina queries diretas às tabelas e limite de 1000 registros
 * 
 * @release v4.0.0 - 2026-03-08
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw, Activity, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FlowBuilderTabs } from '@/components/tenant/flows/FlowBuilderTabs';
import { FlowResultsDashboard, formatDuration } from '@/components/tenant/flows/FlowResultsDashboard';
import { FlowResultsSessionsTable, type NodeColumn } from '@/components/tenant/flows/FlowResultsSessionsTable';
import { FlowFunnelCanvas } from '@/components/tenant/flows/FlowFunnelCanvas';
import { useFlowResultsKpi, type FlowResultsKpiFilters } from '@/hooks/useFlowResultsKpi';
import { useFlowResultsPaginated, type FlowSessionFilters } from '@/hooks/useFlowResultsPaginated';
import { supabase } from '@/integrations/supabase/client';

/**
 * Página principal de resultados do fluxo
 */
export default function FlowResults() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  // Estado para alternar entre sub-views: Tempo Real (funil) e Resultado (sessões/KPIs)
  const [activeView, setActiveView] = useState<'realtime' | 'results'>('realtime');

  // Estado centralizado de filtros (compartilhado entre KPIs e listagem)
  const [filters, setFilters] = useState<FlowSessionFilters>({});
  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filtros para KPIs (sem source, pois KPIs já agregam ambas fontes)
  const kpiFilters: FlowResultsKpiFilters = useMemo(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    inboxId: filters.inboxId,
    triggerType: filters.triggerType,
    status: filters.status,
    search: filters.search,
  }), [filters]);

  // Busca dados básicos do fluxo (incluindo nodes para colunas dinâmicas)
  const { data: flow } = useQuery({
    queryKey: ['flow-results-basic', flowId],
    queryFn: async () => {
      const { data } = await supabase
        .from('flows')
        .select('name, status, public_views_count, nodes')
        .eq('id', flowId)
        .single();
      return data;
    },
    enabled: !!flowId,
  });

  // KPIs consolidados via RPC server-side
  const { kpi, isLoading: isLoadingKpi, refetch: refetchKpi } = useFlowResultsKpi(flowId, kpiFilters);

  // Sessões paginadas via RPC server-side
  const {
    items: sessions,
    totalCount,
    totalPages,
    isLoading: isLoadingSessions,
    isFetching: isFetchingSessions,
    refetch: refetchSessions,
  } = useFlowResultsPaginated(flowId, filters, {
    page: currentPage,
    pageSize,
    sortField: 'started_at',
    sortDirection: 'desc',
  });

  // Extrai colunas dinâmicas dos nodes interativos do fluxo
  const nodeColumns: NodeColumn[] = useMemo(() => {
    if (!flow?.nodes) return [];
    const nodes = flow.nodes as Array<{
      id: string;
      type: string;
      data?: { label?: string; config?: { variableName?: string; question?: string; nickname?: string } };
    }>;
    // Inclui todos os nodes do fluxo (exceto start/end que não geram dados úteis)
    const excludedTypes = ['start', 'end'];
    return nodes
      .filter(n => !excludedTypes.includes(n.type))
      .map(n => ({
        nodeId: n.id,
        label: n.data?.config?.nickname || n.data?.label || n.data?.config?.question || n.data?.config?.variableName || n.type,
        nodeType: n.type,
      }));
  }, [flow?.nodes]);

  const isLoading = isLoadingKpi || isLoadingSessions;

  // Atualiza todas as queries
  const handleRefresh = useCallback(() => {
    refetchKpi();
    refetchSessions();
  }, [refetchKpi, refetchSessions]);

  // Handler de mudança de filtros (reseta paginação)
  const handleFiltersChange = useCallback((newFilters: FlowSessionFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="h-14 border-b border-border bg-card relative flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/tenant/flows')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold">{flow?.name || 'Fluxo'}</h2>
        </div>

        {/* Tabs de navegação - posicionamento absoluto central */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <FlowBuilderTabs />
        </div>

        {/* Toggle entre Tempo Real e Resultado - canto direito */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          <button
            onClick={() => setActiveView('realtime')}
            className={`flex items-center gap-2 rounded-md text-sm font-medium px-3 py-1.5 transition-all ${
              activeView === 'realtime'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Activity className="h-4 w-4" />
            Tempo Real
          </button>
          <button
            onClick={() => setActiveView('results')}
            className={`flex items-center gap-2 rounded-md text-sm font-medium px-3 py-1.5 transition-all ${
              activeView === 'results'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Resultado
          </button>
        </div>
      </div>

      {/* Conteúdo: Tempo Real (funil) ou Resultado (KPIs + Sessões) */}
      {activeView === 'realtime' ? (
        <div className="flex-1 overflow-hidden">
          {flowId && <FlowFunnelCanvas flowId={flowId} />}
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Header premium da view Resultado */}

          {/* KPIs premium via RPC consolidada */}
          <div className="p-4 border-b">
            <FlowResultsDashboard kpi={kpi} formatDuration={formatDuration} />
          </div>

          {/* Tabela unificada de sessões com paginação server-side */}
          <div className="p-4">
            <FlowResultsSessionsTable
              sessions={sessions}
              nodeColumns={nodeColumns}
              flowId={flowId}
              totalCount={totalCount}
              totalPages={totalPages}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              isLoading={isLoadingSessions}
              isFetching={isFetchingSessions}
              onRefresh={handleRefresh}
              isRefreshing={isLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
}
